"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
exports.__esModule = true;
exports.createDataProvider = exports.createDataContext = exports.DataProvider = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var useAsyncRequestState_1 = require("../hooks/useAsyncRequestState");
/**
 * createDataContext is a generic React.Context and useContext hook factory. `T`
 * is the data type for a given data context. For example:
 *
 * type OverviewPageData = {
 *   foo: string,
 *   bar: string
 * }
 *
 * const [OverviewContext, useOverviewContext] = createDataContext<OverviewPageData>();
 */
function createDataContext() {
    var dataContext = (0, react_1.createContext)({
        isLoading: useAsyncRequestState_1.AsyncRequestState.NEVER,
        data: null
    });
    var useDataContext = function () { return (0, react_1.useContext)(dataContext); };
    return [dataContext, useDataContext];
}
exports.createDataContext = createDataContext;
/**
 * DataProvider is a generic component to provide children components with specific API data for a given data context.
 *
 * By default, DataProvider _will not_ render children until it reaches a terminal state of
 * COMPLETE. Error and loading states are managed by DataProvider. If you want children components
 * to manage their own loading state, set useAsync to true.
 *
 * FIXME: Error typing needs to improve before deeper usage. Some additional features may be needed.
 */
function DataProvider(_a) {
    var DataContext = _a.DataContext, getter = _a.getter, useAsync = _a.useAsync, _b = _a.renderLoading, renderLoading = _b === void 0 ? function () { return null; } : _b, _c = _a.renderError, renderError = _c === void 0 ? function () { return null; } : _c, children = _a.children;
    var _d = (0, useAsyncRequestState_1.useAsyncRequestState)(getter), request = _d.request, requestState = _d.requestState, data = _d.data, error = _d.error;
    (0, react_1.useEffect)(function () {
        request();
    }, []);
    if (requestState === useAsyncRequestState_1.AsyncRequestState.ERROR) {
        var errorObj = {
            rawError: error,
            message: (error === null || error === void 0 ? void 0 : error.message) || 'Unknown error'
        };
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: renderError(errorObj) });
    }
    else if (requestState === useAsyncRequestState_1.AsyncRequestState.COMPLETE || (useAsync && useAsyncRequestState_1.AsyncRequestState.LOADING)) {
        return (0, jsx_runtime_1.jsx)(DataContext.Provider, __assign({ value: { data: data, isLoading: requestState } }, { children: children }));
    }
    else {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: renderLoading(requestState) });
    }
}
exports.DataProvider = DataProvider;
/**
 * createDataProvider is a helper to abstract away wiring up a DataProvider. Returns
 * a wrapped DataProvider for the given data type and getter. This provider _will not_
 * render children on anything other than COMPLETE and as `useAsync` set to false.
 */
function createDataProvider(defaultProps) {
    if (defaultProps === void 0) { defaultProps = {}; }
    // Create the context needed for this provider
    var _a = createDataContext(), dataContext = _a[0], useDataContext = _a[1];
    // Create the component used for this specific provider
    var Provider = function (dataProviderProps) {
        var combinedProps = __assign(__assign({}, defaultProps), dataProviderProps);
        return (0, jsx_runtime_1.jsx)(DataProvider, __assign({ DataContext: dataContext, useAsync: false }, combinedProps));
    };
    // useAsync is explicitly false, meaning useDataContext should never have
    // null data. Wrap the return type w/ NonNullable to simplify typing.
    // WARNING: runtime errors are still possible.
    var useNonNullableData = function () { return useDataContext(); };
    return [useNonNullableData, Provider, dataContext];
}
exports.createDataProvider = createDataProvider;
